import { customElement, observable, resolve, type IDisposable, IEventAggregator } from 'aurelia';
import { route } from '@aurelia/router';
import { I18N, Signals } from '@aurelia/i18n';
import template from './documents-list.html';
import type { Document, Tag, ChronologyTree, SecurityLevel, SharedUser, RuleApplicationResponse, DocumentFile } from '../services/api.types';
import { SECURITY_LEVELS } from '../services/api.types';
import {
  listDocuments,
  getDocument,
  deleteDocument,
  getChronologyTree,
  createDocument,
  updateDocument,
  uploadFile,
  applyRulesToDocument,
  getMe,
  getUserSettings,
  upsertUserSetting,
  createImportProtocol,
  completeImportProtocol,
  addImportProtocolEntry,
} from '../services/api.service';
import { ConfirmDialog, type DuplicateResultItem } from '../components/confirm-dialog';
import { apiUrl } from '../services/runtime-config';

const COLUMNS_STORAGE_KEY = 'radish_visible_columns';
const UI_STATE_KEY = 'radish_documents_ui';
const COLUMNS_SETTING_KEY = 'documents.visibleColumns';
const UI_STATE_SETTING_KEY = 'documents.uiState';
const SELECT_ALL_CONFIRM_THRESHOLD = 100;

type DocumentSelectionAction = 'applyRule' | 'share';

export const COLUMN_DEFS = [
  { key: 'title',         i18nKey: 'documents.columns.title' },
  { key: 'tags',          i18nKey: 'documents.columns.tags' },
  { key: 'documentDate',  i18nKey: 'documents.columns.documentDate' },
  { key: 'createdAt',     i18nKey: 'documents.columns.createdAt' },
  { key: 'lastUpdatedAt', i18nKey: 'documents.columns.lastChanged' },
  { key: 'files',         i18nKey: 'documents.columns.files' },
  { key: 'followUps',     i18nKey: 'documents.columns.followUps' },
  { key: 'securityLevel', i18nKey: 'documents.securityLevel.label' },
] as const;

const DEFAULT_COLUMNS: Record<string, boolean> = {
  title: true,
  tags: true,
  documentDate: true,
  createdAt: true,
  lastUpdatedAt: false,
  files: true,
  followUps: true,
  securityLevel: false,
};

interface PersistedDocumentUiState {
  showChronology?: boolean;
  showFilterBar?: boolean;
  ownedOnly?: boolean;
  filterText?: string;
  filterIncludeDescription?: boolean;
  filterIncludeFileText?: boolean;
  includeFormOnly?: boolean;
  filterTags?: Tag[];
  shareFilterType?: 'none' | 'from' | 'with';
  shareFilterUsers?: SharedUser[];
  viewMode?: 'list' | 'tiles';
  sortBy?: string;
  sortDescending?: boolean;
}


@route({ path: ['', 'documents'], title: 'Documents' })
@customElement({ name: 'documents-list', template })
export class DocumentsList {
  private readonly i18n: I18N = resolve(I18N);
  private readonly ea: IEventAggregator = resolve(IEventAggregator);
  private _localeSub: IDisposable | null = null;

  // Tracked so dirty-checking re-evaluates monthName() and breadcrumbItems when locale changes
  public _locale = '';
  public documents: Document[] = [];
  public totalCount = 0;
  public sortBy = 'documentDate';
  public sortDescending = true;
  public page = 1;
  public pageSize = 25;
  public isFetching = false;
  public fetchError = '';
  public showAddModal = false;
  public showFormDocumentModal = false;
  public showShareModal = false;
  public showBatchRuleDialog = false;
  public viewDocument: Document | null = null;
  public editDocument: Document | null = null;
  public currentUsername = '';
  public viewMode: 'list' | 'tiles' = 'list';

  /** Files pre-populated via drop → open-add-modal (scenario 3a) */
  public initialFilesForAddModal: File[] = [];
  /** Files pre-populated via drop → open-edit-modal (scenario 1) */
  public initialFilesForEditModal: File[] = [];

  /** Global drag-from-OS tracking for showing drop zones */
  public isDraggingFile = false;
  public dropZoneOneDocActive = false;
  public dropZonePerFileActive = false;
  /** While batch-creating documents (scenario 3b) */
  public isBatchCreating = false;
  /** Protocol for the current external drag-and-drop modal operation. */
  public pendingImportProtocolNumber: number | null = null;

  public get showDropOverlay(): boolean {
    return this.isDraggingFile &&
      !this.showAddModal &&
      !this.showFormDocumentModal &&
      this.viewDocument === null &&
      this.editDocument === null;
  }

  private hasExternalDragPayload(e: DragEvent): boolean {
    const dataTransfer = e.dataTransfer;
    if (!dataTransfer) return false;
    return !Array.from(dataTransfer.types)
      .some(type => type.toLowerCase() === 'application/x-radish-reorder');
  }

  // Row selection
  public selectedIds: Set<string> = new Set();
  private _lastSelectedIdx = -1;
  public rangeSelectionMode = false;
  public isSelectingAll = false;
  public selectAllProgress = 0;
  public selectAllTotal = 0;
  public selectionActionsOpen = false;
  public selectionActionsHost: HTMLElement | null = null;
  private readonly _selectionDocumentHandler = (event: MouseEvent): void => {
    const target = event.target;
    if (target instanceof Node && !this.selectionActionsHost?.contains(target)) {
      this.selectionActionsOpen = false;
    }
  };

  // Chronology tree state
  public showChronology = false;
  public chronologyTree: ChronologyTree | null = null;
  public expandedYears: Record<number, boolean> = {};
  public expandedMonths: Record<string, boolean> = {};
  public selType: 'all' | 'year' | 'month' | 'day' = 'all';
  public selYear = 0;
  public selMonth = 0;
  public selDay = 0;

  // Column visibility
  public readonly allColumns = COLUMN_DEFS;
  public visibleColumns: Record<string, boolean> = { ...DEFAULT_COLUMNS };
  public showColumnPicker = false;

  // Filters
  public showFilterBar = false;
  public ownedOnly = false;
  public filterText = '';
  public filterIncludeDescription = false;
  public filterIncludeFileText = false;
  public includeFormOnly = false;
  @observable public filterTags: Tag[] = [];
  public shareFilterType: 'none' | 'from' | 'with' = 'none';
  @observable public shareFilterUsers: SharedUser[] = [];
  private _filterDebounce: ReturnType<typeof setTimeout> | null = null;
  private _preferenceSyncTimer: ReturnType<typeof setTimeout> | null = null;
  private _ignoreShareFilterUsersChanged = false;
  private _batchRuleNeedsRefresh = false;

  public get hasSelection(): boolean { return this.selectedIds.size > 0; }
  public get selectionCount(): number { return this.selectedIds.size; }
  public get canSelectRange(): boolean {
    return this.hasSelection &&
      this._lastSelectedIdx >= 0 &&
      this._lastSelectedIdx < this.documents.length;
  }
  public get rangeSelectionLabel(): string {
    return this.i18n.tr(this.rangeSelectionMode
      ? 'documents.selection.cancelRange'
      : 'documents.selection.selectRange');
  }
  public get selectedIdsArray(): string[] { return [...this.selectedIds]; }
  public get selectedDocuments(): Document[] {
    return this.documents.filter(document => this.selectedIds.has(document.id));
  }
  public get canApplyRuleSelection(): boolean {
    return this.selectedDocuments.length > 0 &&
      this.selectedDocuments.every(document => this.isDocumentOwner(document));
  }
  public get canShareSelection(): boolean {
    if (this.selectedIds.size === 0) return false;
    return this.documents
      .filter(d => this.selectedIds.has(d.id))
      .every(d => this.isDocumentOwner(d));
  }
  public get canDeleteSelection(): boolean {
    if (this.selectedIds.size === 0) return false;
    return this.documents
      .filter(d => this.selectedIds.has(d.id))
      .every(d => this.isDocumentOwner(d));
  }
  public get hasActiveFilter(): boolean {
    return this.filterText.trim().length > 0
      || this.filterTags.length > 0
      || this.includeFormOnly
      || (this.shareFilterType !== 'none' && this.shareFilterUsers.length > 0);
  }

  public displayName(username?: string | null, name?: string | null): string {
    return name || username || '';
  }

  public sharedByTooltip(doc: Document): string {
    return this.i18n.tr('documents.sharedBy', {
      name: this.displayName(doc.owner, doc.ownerDisplayName),
    });
  }

  public sharedWithTooltip(doc: Document): string {
    return this.i18n.tr('documents.sharedWithTooltip', {
      names: doc.sharedWith.map(u => u.displayName ?? u.username).join(', '),
    });
  }

  public binding(): void {
    this._locale = this.i18n.getLocale();
    this._localeSub = this.ea.subscribe(Signals.I18N_EA_CHANNEL, () => {
      this._locale = this.i18n.getLocale();
      // Deep-clone the tree so repeat.for sees new object references and
      // re-evaluates monthName() calls inside the nested repeaters.
      if (this.chronologyTree) {
        this.chronologyTree = JSON.parse(JSON.stringify(this.chronologyTree)) as ChronologyTree;
      }
    });
  }

  public attached(): void {
    if (this.scrollSentinel && this.scrollContainer) {
      this._scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) void this._loadMore();
        },
        { root: this.scrollContainer, threshold: 0.1 },
      );
      this._scrollObserver.observe(this.scrollSentinel);
    }

    document.addEventListener('dragenter', this._onGlobalDragEnter);
    document.addEventListener('dragover', this._onGlobalDragOver);
    document.addEventListener('dragleave', this._onGlobalDragLeave);
    document.addEventListener('drop', this._onGlobalDrop);
    document.addEventListener('click', this._selectionDocumentHandler);
  }

  get visibleColumnCount(): number {
    return Object.values(this.visibleColumns).filter(Boolean).length;
  }

  public toggleColumnPicker(): void {
    this.showColumnPicker = !this.showColumnPicker;
  }

  public closeColumnPicker(): void {
    this.showColumnPicker = false;
  }

  public toggleColumn(key: string): void {
    this.visibleColumns = { ...this.visibleColumns, [key]: !this.visibleColumns[key] };
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(this.visibleColumns));
    this._schedulePreferenceSync();
  }

  public async loading(): Promise<void> {
    const me = await getMe().catch(() => null);
    this.currentUsername = me?.username ?? '';
    const localPreferences = this._loadCachedPreferences();

    const settings = await getUserSettings().catch(() => []);
    const serverColumns = settings.find(setting => setting.key === COLUMNS_SETTING_KEY);
    const serverUiState = settings.find(setting => setting.key === UI_STATE_SETTING_KEY);

    if (serverColumns) {
      this._applyColumnsSetting(serverColumns.value);
    }
    if (serverUiState) {
      this._applyUiStateSetting(serverUiState.value);
    }

    // Migrate the existing browser-only preferences to the account the first time
    // the documents view is opened after this version.
    if ((!serverColumns && localPreferences.hasColumns) || (!serverUiState && localPreferences.hasUiState)) {
      this._schedulePreferenceSync();
    }

    await this.fetchDocuments();
    if (this.showChronology) void this.reloadChronologyTree();
    const openDocumentId = new URLSearchParams(window.location.search).get('openDocumentId');
    if (openDocumentId) {
      try {
        this.openViewModal(await getDocument(openDocumentId));
      } catch {
        // The documents view remains usable when the requested protocol document
        // was deleted before the link was opened.
      }
    }
  }

  private get _filterTagIds(): string[] {
    return this.filterTags.filter(t => t.id).map(t => t.id);
  }

  public async fetchDocuments(): Promise<void> {
    this.page = 1;
    this.isFetching = true;
    this.fetchError = '';
    this.selectedIds = new Set();
    this._lastSelectedIdx = -1;
    this.rangeSelectionMode = false;
    this.selectionActionsOpen = false;
    try {
      const { dateFrom, dateTo } = this.selectionDateRange();
      const searchText = this.filterText.trim() || undefined;
      const shareFilterType = this._shareFilterTypeValue;
      const shareFilterUsernames = this._shareFilterUsernames;
      const result = await listDocuments(
        this.sortBy, this.sortDescending, this.page, this.pageSize,
        dateFrom, dateTo,
        searchText, this.filterIncludeDescription,
        this._filterTagIds.length > 0 ? this._filterTagIds : undefined,
        this.ownedOnly,
        shareFilterType,
        shareFilterUsernames,
        this.filterIncludeFileText,
        this.includeFormOnly,
      );
      this.documents = result.documents;
      this.totalCount = result.totalCount;
    } catch (err: unknown) {
      this.fetchError = err instanceof Error ? err.message : this.i18n.tr('documents.load.failed');
    } finally {
      this.isFetching = false;
    }
  }

  /**
   * Refreshes the document list while preserving the current scroll position and
   * the number of already-loaded documents (all pages loaded so far are re-fetched
   * in a single request so the user stays exactly where they were).
   */
  private async fetchDocumentsPreservingScroll(): Promise<void> {
    const savedPage = this.page;                        // e.g. 5 if user scrolled to page 5
    const savedScrollTop = this.scrollContainer?.scrollTop ?? 0;
    const loadedPageSize = savedPage * this.pageSize;   // fetch all rows that were visible

    this.isFetching = true;
    this.fetchError = '';
    this.selectedIds = new Set();
    this._lastSelectedIdx = -1;
    this.rangeSelectionMode = false;
    this.selectionActionsOpen = false;
    try {
      const { dateFrom, dateTo } = this.selectionDateRange();
      const searchText = this.filterText.trim() || undefined;
      const shareFilterType = this._shareFilterTypeValue;
      const shareFilterUsernames = this._shareFilterUsernames;
      const result = await listDocuments(
        this.sortBy, this.sortDescending, 1, loadedPageSize,
        dateFrom, dateTo,
        searchText, this.filterIncludeDescription,
        this._filterTagIds.length > 0 ? this._filterTagIds : undefined,
        this.ownedOnly,
        shareFilterType,
        shareFilterUsernames,
        this.filterIncludeFileText,
        this.includeFormOnly,
      );
      this.documents = result.documents;
      this.totalCount = result.totalCount;
      // Keep page counter at savedPage so _loadMore continues from the right offset
      this.page = savedPage;
    } catch (err: unknown) {
      this.fetchError = err instanceof Error ? err.message : this.i18n.tr('documents.load.failed');
      this.page = 1;
    } finally {
      this.isFetching = false;
    }

    // Restore scroll position after Aurelia has updated the DOM
    requestAnimationFrame(() => {
      if (this.scrollContainer) {
        this.scrollContainer.scrollTop = savedScrollTop;
      }
    });
  }

  // ── Filters ────────────────────────────────────────────────────────────────

  public toggleFilterBar(): void {
    this.showFilterBar = !this.showFilterBar;
    this._saveUiState();
  }

  public toggleOwnedOnly(): void {
    this.ownedOnly = !this.ownedOnly;
    this.page = 1;
    this.fetchDocuments();
    if (this.showChronology) void this.reloadChronologyTree();
    this._saveUiState();
  }

  public onFilterTextInput(event: Event): void {
    this.filterText = (event.target as HTMLInputElement).value;
    this._scheduleFilterFetch();
  }

  public clearFilterText(): void {
    this.filterText = '';
    this._scheduleFilterFetch();
  }

  public onFilterIncludeDescriptionChange(): void {
    if (this.filterText.trim()) this._scheduleFilterFetch();
  }

  public onFilterIncludeFileTextChange(): void {
    if (this.filterText.trim()) this._scheduleFilterFetch();
  }

  public onIncludeFormOnlyChange(): void {
    this.applyFilters();
  }

  public onFilterTagsChanged(): void {
    this._scheduleFilterFetch();
  }

  // Called by @observable when filterTags reference changes (tag-input replaces the array)
  public filterTagsChanged(): void {
    this._scheduleFilterFetch();
  }

  public clearFilterTags(): void {
    this.filterTags = [];
    this._scheduleFilterFetch();
  }

  public onShareFilterTypeChange(): void {
    this._setShareFilterUsers([]);
    this.applyFilters();
  }

  public onShareFilterUsersChange(): void {
    this.applyFilters();
  }

  public shareFilterUsersChanged(): void {
    if (this._ignoreShareFilterUsersChanged) return;
    this.onShareFilterUsersChange();
  }

  public clearShareFilterUsers(): void {
    this._setShareFilterUsers([]);
    this.applyFilters();
  }

  public clearShareFilter(): void {
    this.shareFilterType = 'none';
    this._setShareFilterUsers([]);
    this.applyFilters();
  }

  private _setShareFilterUsers(users: SharedUser[]): void {
    this._ignoreShareFilterUsersChanged = true;
    this.shareFilterUsers = users;
    this._ignoreShareFilterUsersChanged = false;
  }

  private get _shareFilterTypeValue(): number | undefined {
    if (this.shareFilterUsers.length === 0) return undefined;
    if (this.shareFilterType === 'from') return 1;
    if (this.shareFilterType === 'with') return 2;
    return undefined;
  }

  private get _shareFilterUsernames(): string[] | undefined {
    if (this.shareFilterType === 'none' || this.shareFilterUsers.length === 0) return undefined;
    return this.shareFilterUsers.map(user => user.username);
  }

  private applyFilters(): void {
    if (this._filterDebounce !== null) {
      clearTimeout(this._filterDebounce);
      this._filterDebounce = null;
    }
    this._saveUiState();
    this.page = 1;
    void this.fetchDocuments();
    if (this.showChronology) void this.reloadChronologyTree();
  }

  private _scheduleFilterFetch(): void {
    if (this._filterDebounce !== null) clearTimeout(this._filterDebounce);
    this._filterDebounce = setTimeout(() => {
      this._filterDebounce = null;
      this.applyFilters();
    }, 300);
  }

  public sortBy_(column: string): void {
    if (this.sortBy === column) {
      this.sortDescending = !this.sortDescending;
    } else {
      this.sortBy = column;
      this.sortDescending = true;
    }
    this.page = 1;
    this._saveUiState();
    void this.fetchDocuments();
  }

  public sortIndicator(column: string): string {
    if (this.sortBy !== column) return '';
    return this.sortDescending ? '▼' : '▲';
  }

  // ── Row selection ──────────────────────────────────────────────────────────

  // Touch long-press state (iPad multi-select)
  private _touchTimer: ReturnType<typeof setTimeout> | null = null;
  private _touchMoved = false;
  // Double-tap detection
  private _tapTimer: ReturnType<typeof setTimeout> | null = null;
  private _lastTapDocId = '';
  private _lastTapTime = 0;

  public onTouchStart(doc: Document, event: TouchEvent): void {
    this._touchMoved = false;
    this._touchTimer = setTimeout(() => {
      this._touchTimer = null;
      // Long press → open document
      this.openViewModal(doc);
    }, 600);
  }

  public onTouchMove(): void {
    this._touchMoved = true;
    if (this._touchTimer !== null) {
      clearTimeout(this._touchTimer);
      this._touchTimer = null;
    }
  }

  public onTouchEnd(doc: Document, index: number, event: TouchEvent): void {
    if (this._touchTimer !== null) {
      clearTimeout(this._touchTimer);
      this._touchTimer = null;
    }
    if (this._touchMoved) return;
    event.preventDefault(); // suppress ghost click

    const now = Date.now();
    const isDoubleTap = this._lastTapDocId === doc.id && (now - this._lastTapTime) < 350;

    if (isDoubleTap) {
      // Cancel pending single-tap toggle, open instead
      if (this._tapTimer !== null) {
        clearTimeout(this._tapTimer);
        this._tapTimer = null;
      }
      this._lastTapDocId = '';
      this._lastTapTime = 0;
      this.openViewModal(doc);
    } else {
      this._lastTapDocId = doc.id;
      this._lastTapTime = now;
      // Delay toggle to wait for potential double-tap
      if (this._tapTimer !== null) clearTimeout(this._tapTimer);
      this._tapTimer = setTimeout(() => {
        this._tapTimer = null;
        if (this.rangeSelectionMode && this._lastSelectedIdx >= 0) {
          this.selectDocumentRange(this._lastSelectedIdx, index);
          return;
        }
        const next = new Set(this.selectedIds);
        if (next.has(doc.id)) next.delete(doc.id); else next.add(doc.id);
        this.selectedIds = next;
        this._lastSelectedIdx = index;
      }, 350);
    }
  }

  public clearSelection(): void {
    this.selectedIds = new Set();
    this._lastSelectedIdx = -1;
    this.rangeSelectionMode = false;
    this.selectionActionsOpen = false;
  }

  public toggleSelectionActions(event: Event): void {
    event.stopPropagation();
    if (!this.hasSelection || this.isSelectingAll) return;
    this.selectionActionsOpen = !this.selectionActionsOpen;
  }

  public onSelectionActionsKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.selectionActionsOpen = false;
    this.selectionActionsHost?.querySelector<HTMLButtonElement>('.selection-bar__icon-button')?.focus();
  }

  public runSelectionAction(action: DocumentSelectionAction): void {
    if (!this.hasSelection || this.isSelectingAll) return;
    this.selectionActionsOpen = false;
    switch (action) {
      case 'applyRule':
        this.openBatchRuleDialog();
        break;
      case 'share':
        this.openShareModal();
        break;
    }
  }

  public toggleRangeSelectionMode(): void {
    if (this.isSelectingAll || !this.canSelectRange) return;
    this.rangeSelectionMode = !this.rangeSelectionMode;
  }

  private selectDocumentRange(anchorIndex: number, targetIndex: number): void {
    const start = Math.min(anchorIndex, targetIndex);
    const end = Math.max(anchorIndex, targetIndex);
    const next = new Set<string>();
    for (let index = start; index <= end; index++) {
      const document = this.documents[index];
      if (document) next.add(document.id);
    }
    this.selectedIds = next;
    this._lastSelectedIdx = targetIndex;
    this.rangeSelectionMode = false;
  }

  public async selectAllVisible(): Promise<void> {
    if (this.isSelectingAll || this.isFetching || this.isLoadingMore) return;

    const totalToSelect = this.totalCount;
    if (totalToSelect > SELECT_ALL_CONFIRM_THRESHOLD) {
      const confirmed = await ConfirmDialog.confirm(
        this.i18n.tr('documents.selection.selectAllConfirm', { count: totalToSelect }),
        this.i18n.tr('documents.selection.selectAllTitle'),
      );
      if (!confirmed) return;
    }

    this.isSelectingAll = true;
    this.selectAllProgress = Math.min(this.documents.length, totalToSelect);
    this.selectAllTotal = totalToSelect;

    try {
      const loadDocumentsPage = (page: number, pageSize: number): Promise<Awaited<ReturnType<typeof listDocuments>>> => {
        const { dateFrom, dateTo } = this.selectionDateRange();
        return listDocuments(
          this.sortBy,
          this.sortDescending,
          page,
          pageSize,
          dateFrom,
          dateTo,
          this.filterText.trim() || undefined,
          this.filterIncludeDescription,
          this._filterTagIds.length > 0 ? this._filterTagIds : undefined,
          this.ownedOnly,
          this._shareFilterTypeValue,
          this._shareFilterUsernames,
          this.filterIncludeFileText,
          this.includeFormOnly,
        );
      };

      while (this.documents.length < this.totalCount) {
        const nextPage = this.page + 1;
        const result = await loadDocumentsPage(nextPage, this.pageSize);
        const existingIds = new Set(this.documents.map(document => document.id));
        const newDocuments = result.documents.filter(document => !existingIds.has(document.id));
        this.totalCount = result.totalCount;
        this.selectAllTotal = this.totalCount;

        if (newDocuments.length === 0) {
          if (this.documents.length >= this.totalCount) break;
          const recovery = await loadDocumentsPage(1, Math.max(this.totalCount, this.documents.length));
          const recoveryDocuments = [...new Map(
            recovery.documents.map(document => [document.id, document]),
          ).values()];
          this.documents = recoveryDocuments;
          this.totalCount = recovery.totalCount;
          this.selectAllTotal = this.totalCount;
          this.selectAllProgress = Math.min(this.documents.length, this.selectAllTotal);
          this.page = Math.max(1, Math.ceil(this.documents.length / this.pageSize));
          if (this.documents.length >= this.totalCount) break;
          throw new Error(this.i18n.tr('documents.selection.selectAllFailed', {
            page: nextPage,
            returned: result.documents.length,
            loaded: this.documents.length,
            total: this.totalCount,
          }));
        }

        this.documents = [...this.documents, ...newDocuments];
        this.page = nextPage;
        this.selectAllProgress = Math.min(this.documents.length, this.selectAllTotal);
      }

      this.selectedIds = new Set(this.documents.map(document => document.id));
      this._lastSelectedIdx = -1;
      this.rangeSelectionMode = false;
      this.selectAllProgress = this.documents.length;
      this.selectAllTotal = this.documents.length;
    } catch (error: unknown) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : String(error),
        this.i18n.tr('documents.selection.selectAllTitle'),
      );
    } finally {
      this.isSelectingAll = false;
    }
  }

  public onRowClick(doc: Document, index: number, event: MouseEvent): void {
    if (this.rangeSelectionMode && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectDocumentRange(this._lastSelectedIdx, index);
    } else if (event.ctrlKey || event.metaKey) {
      const next = new Set(this.selectedIds);
      if (next.has(doc.id)) next.delete(doc.id); else next.add(doc.id);
      this.selectedIds = next;
      this._lastSelectedIdx = index;
    } else if (event.shiftKey && this._lastSelectedIdx >= 0) {
      event.preventDefault();
      this.selectDocumentRange(this._lastSelectedIdx, index);
    } else {
      this.selectedIds = new Set([doc.id]);
      this._lastSelectedIdx = index;
    }
  }

  public onRowDblClick(doc: Document, event: MouseEvent): boolean {
    if (event.ctrlKey && this.isDocumentOwner(doc)) {
      this.openEditModal(doc);
    } else {
      this.openViewModal(doc);
    }
    return false;
  }

  public isDocumentOwner(doc: Document): boolean {
    return !doc.owner || doc.owner === this.currentUsername;
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  public async deleteSelected(): Promise<void> {
    if (!this.canDeleteSelection) return;

    const selectedDocuments = this.documents.filter(d => this.selectedIds.has(d.id));
    const warningMessage = this.buildDeleteOthersFollowUpsWarning(selectedDocuments);
    if (warningMessage) {
      const warned = await ConfirmDialog.confirm(warningMessage, this.i18n.tr('documents.delete.title'));
      if (!warned) return;
    }

    let message: string;
    if (this.selectedIds.size === 1) {
      const doc = selectedDocuments[0];
      message = this.i18n.tr('documents.delete.confirmOne', { title: doc?.title ?? '' });
    } else {
      message = this.i18n.tr('documents.delete.confirmMany', { count: this.selectedIds.size });
    }

    const confirmed = await ConfirmDialog.confirm(message, this.i18n.tr('documents.delete.title'));
    if (!confirmed) return;
    try {
      for (const id of this.selectedIds) {
        await deleteDocument(id);
      }
      this.selectedIds = new Set();
      await this.fetchDocumentsPreservingScroll();
      if (this.showChronology) await this.reloadChronologyTree();
    } catch (err: unknown) {
      await ConfirmDialog.alert(err instanceof Error ? err.message : this.i18n.tr('documents.delete.failed'), this.i18n.tr('common.error'));
    }
  }

  private buildDeleteOthersFollowUpsWarning(documents: Document[]): string | null {
    const currentUsername = this.currentUsername.toLowerCase();
    const details = documents
      .map(doc => {
        const usernames = Array.from(new Set(
          (doc.followUps ?? [])
            .map(fu => fu.forUser)
            .filter((username): username is string => !!username && username.toLowerCase() !== currentUsername)
        )).sort((a, b) => a.localeCompare(b));

        if (usernames.length === 0) return null;
        return `- "${doc.title}" (reminders for: ${usernames.join(', ')})`;
      })
      .filter((line): line is string => !!line)
      .join('\n');

    if (!details) return null;
    return this.i18n.tr('documents.delete.hasOthersFollowUps').replace('{details}', details);
  }

  public openAddModal(): void {
    this.showAddModal = true;
  }

  public openFormDocumentModal(): void {
    this.showFormDocumentModal = true;
  }

  public openShareModal(): void {
    this.showShareModal = true;
  }

  public openBatchRuleDialog(): void {
    if (!this.canApplyRuleSelection) return;
    this.showBatchRuleDialog = true;
    this._batchRuleNeedsRefresh = false;
  }

  public onBatchRuleCompleted(changed: boolean): void {
    this._batchRuleNeedsRefresh ||= changed;
  }

  public async onBatchRuleDialogClosed(): Promise<void> {
    this.showBatchRuleDialog = false;
    if (!this._batchRuleNeedsRefresh) return;
    this._batchRuleNeedsRefresh = false;
    await this.fetchDocumentsPreservingScroll();
    if (this.showChronology) await this.reloadChronologyTree();
  }

  public openEditModal(doc: Document): void {
    if (!this.isDocumentOwner(doc)) return;
    this.editDocument = doc;
    this.viewDocument = null;
  }

  public openViewModal(doc: Document): void {
    this.viewDocument = doc;
  }

  public async onEditFromView(doc: Document, files?: File[]): Promise<void> {
    if (files?.length) {
      try {
        this.pendingImportProtocolNumber = await this.startImportProtocol();
      } catch (error: unknown) {
        await ConfirmDialog.alert(
          error instanceof Error ? error.message : this.i18n.tr('documents.dropZone.createError'),
          this.i18n.tr('common.error'),
        );
        return;
      }
      this.initialFilesForEditModal = [...files];
    }
    this.editDocument = doc;
    this.viewDocument = null;
  }

  public onViewClosed(): void {
    this.viewDocument = null;
  }

  public async onDocumentDeleted(): Promise<void> {
    await this.finishPendingImportProtocol();
    this.editDocument = null;
    this.viewDocument = null;
    this.initialFilesForEditModal = [];
    this.selectedIds = new Set();
    await this.fetchDocumentsPreservingScroll();
    if (this.showChronology) await this.reloadChronologyTree();
  }

  public onViewDocumentUpdated(doc: Document): void {
    this.viewDocument = doc;
    this.documents = this.documents.map(d => d.id === doc.id ? doc : d);
  }

  public async onDocumentSaved(): Promise<void> {
    await this.finishPendingImportProtocol();
    this.showAddModal = false;
    this.showFormDocumentModal = false;
    this.showShareModal = false;
    this.editDocument = null;
    this.initialFilesForAddModal = [];
    this.initialFilesForEditModal = [];
    await this.fetchDocumentsPreservingScroll();
    if (this.showChronology) await this.reloadChronologyTree();
  }

  public async onModalClosed(): Promise<void> {
    await this.finishPendingImportProtocol();
    this.showAddModal = false;
    this.showFormDocumentModal = false;
    this.showShareModal = false;
    this.editDocument = null;
    this.viewDocument = null;
    this.initialFilesForAddModal = [];
    this.initialFilesForEditModal = [];
  }

  private async finishPendingImportProtocol(): Promise<void> {
    const number = this.pendingImportProtocolNumber;
    this.pendingImportProtocolNumber = null;
    if (number !== null) {
      await this.finishImportProtocol(number);
    }
  }

  public onFormDocumentSaved(): Promise<void> {
    return this.onDocumentSaved();
  }

  public onFormDocumentModalClosed(): void {
    this.showFormDocumentModal = false;
  }

  public onShareModalClosed(): void {
    this.showShareModal = false;
  }

  public async onShareModalSaved(): Promise<void> {
    this.showShareModal = false;
    await this.fetchDocumentsPreservingScroll();
    if (this.showChronology) await this.reloadChronologyTree();
  }

  // ── OS drag-and-drop: global tracking + drop zones ────────────────────────

  private readonly _onGlobalDragEnter = (e: DragEvent): void => {
    if (this.hasExternalDragPayload(e)) {
      this.isDraggingFile = true;
    }
  };

  private readonly _onGlobalDragOver = (e: DragEvent): void => {
    if (!this.hasExternalDragPayload(e)) return;
    e.preventDefault();
    this.isDraggingFile = true;
  };

  private readonly _onGlobalDragLeave = (e: DragEvent): void => {
    if (!this.isDraggingFile) return;
    // Reset when cursor leaves the browser window entirely
    if (e.relatedTarget === null) {
      this.isDraggingFile = false;
      this.dropZoneOneDocActive = false;
      this.dropZonePerFileActive = false;
    }
  };

  private readonly _onGlobalDrop = (): void => {
    this.isDraggingFile = false;
  };

  public onDragOverOneDoc(e: DragEvent): void {
    const dataTransfer = e.dataTransfer;
    if (!dataTransfer || !this.hasExternalDragPayload(e)) return;
    e.preventDefault();
    dataTransfer.dropEffect = 'copy';
    this.dropZoneOneDocActive = true;
  }

  public onDragLeaveOneDoc(): void {
    this.dropZoneOneDocActive = false;
  }

  public async onDropOneDoc(e: DragEvent): Promise<void> {
    e.preventDefault();
    this.dropZoneOneDocActive = false;
    const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
    if (files.length === 0) return;
    try {
      this.pendingImportProtocolNumber = await this.startImportProtocol();
      this.initialFilesForAddModal = files;
      this.showAddModal = true;
    } catch (error: unknown) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : this.i18n.tr('documents.dropZone.createError'),
        this.i18n.tr('common.error'),
      );
    }
  }

  public onDragOverPerFile(e: DragEvent): void {
    const dataTransfer = e.dataTransfer;
    if (!dataTransfer || !this.hasExternalDragPayload(e)) return;
    e.preventDefault();
    dataTransfer.dropEffect = 'copy';
    this.dropZonePerFileActive = true;
  }

  public onDragLeavePerFile(): void {
    this.dropZonePerFileActive = false;
  }

  public async onDropPerFile(e: DragEvent): Promise<void> {
    e.preventDefault();
    this.dropZonePerFileActive = false;
    const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
    if (files.length === 0) return;
    this.isBatchCreating = true;
    const duplicateResults: DuplicateResultItem[] = [];
    let protocolNumber: number | null = null;
    try {
      protocolNumber = await this.startImportProtocol();
      const today = new Date().toISOString();
      const filesInOrder = files.sort((left, right) => left.name.localeCompare(
        right.name,
        undefined,
        { numeric: true, sensitivity: 'base' },
      ));
      for (const file of filesInOrder) {
        const startedAt = new Date().toISOString();
        let doc: Document | null = null;
        let uploaded: DocumentFile | null = null;
        let ruleResponse: RuleApplicationResponse | null = null;
        const title = file.name.replace(/\.[^/.]+$/, '') || file.name;
        try {
          doc = await createDocument({
            title,
            description: '',
            documentDate: today,
            tagNames: [],
            securityLevel: 1,
          });
          uploaded = await uploadFile(doc.id, file);
          let duplicateResult: DuplicateResultItem | null = null;
          if (uploaded.isDuplicate) {
            const note = this.i18n.tr('documents.form.duplicateFileNote', {
              fileName: uploaded.fileName,
              duplicateFileName: uploaded.duplicateFileName ?? uploaded.fileName,
              documentTitle: uploaded.duplicateDocumentTitle ?? '',
            });
            await updateDocument(doc.id, {
              title,
              description: note,
              documentDate: today,
              tagNames: [],
              securityLevel: 1,
            });
            duplicateResult = {
              fileName: uploaded.fileName,
              status: uploaded.duplicateSkipped ? 'rejected' : 'added',
              statusText: this.i18n.tr(uploaded.duplicateSkipped
                ? 'documents.form.duplicateFileRejectedStatus'
                : 'documents.form.duplicateFileAddedToNewDocumentStatus'),
              detail: this.i18n.tr(uploaded.duplicateSkipped
                ? 'documents.form.duplicateFileSkippedWarning'
                : 'documents.form.duplicateFileAddedDetail', {
                fileName: uploaded.fileName,
                duplicateFileName: uploaded.duplicateFileName ?? uploaded.fileName,
                documentTitle: uploaded.duplicateDocumentTitle ?? '',
              }),
            };
          }
          // Apply server-side rules after the file is stored (best-effort; don't block on failure)
          let movedToExistingDocument = false;
          try {
            ruleResponse = await applyRulesToDocument(doc.id);
            console.info('[Radish DMS] Automatic rule result', {
              fileName: file.name,
              documentId: doc.id,
              ...ruleResponse,
            });
            if (ruleResponse.error) {
              console.warn('[Radish DMS] Automatic rule reported an error', {
                fileName: file.name,
                documentId: doc.id,
                error: ruleResponse.error,
              });
            }
          } catch (ruleError: unknown) {
            console.warn('[Radish DMS] Automatic rule request failed', {
              fileName: file.name,
              documentId: doc.id,
              error: ruleError,
            });
          }
          if (ruleResponse?.duplicateSkipped === true) {
            if (ruleResponse.sourceDocumentDeleted !== true) {
              await deleteDocument(doc.id);
            }
          } else if (ruleResponse) {
            movedToExistingDocument = ruleResponse.fileMoved === true &&
              !!ruleResponse.targetDocumentId;
          }
          if (ruleResponse?.duplicateSkipped === true) {
            duplicateResult = {
              fileName: uploaded.fileName,
              status: 'rejected',
              statusText: this.i18n.tr(
                'documents.form.duplicateFileRejectedStatus',
              ),
              detail: this.i18n.tr(
                'documents.form.duplicateFileRejectedByRule',
              ),
            };
          }
          if (duplicateResult) {
            if (movedToExistingDocument && duplicateResult.status === 'added') {
              duplicateResult.statusText = this.i18n.tr(
                'documents.form.duplicateFileAddedToExistingDocumentStatus',
              );
            }
            duplicateResults.push(duplicateResult);
          }

          const duplicateRejected = uploaded.duplicateSkipped === true ||
            ruleResponse?.duplicateSkipped === true;
          const moved = ruleResponse?.fileMoved === true && !!ruleResponse.targetDocumentId;
          await this.recordImportProtocolEntry(protocolNumber, {
            originalFileName: file.name,
            startedAt,
            completedAt: new Date().toISOString(),
            documentFileId: uploaded.id,
            sourceDocumentId: doc.id,
            sourceDocumentTitle: title,
            currentDocumentId: duplicateRejected && ruleResponse?.duplicateSkipped
              ? null
              : moved
                ? ruleResponse?.targetDocumentId
                : doc.id,
            currentDocumentTitle: duplicateRejected && ruleResponse?.duplicateSkipped
              ? null
              : moved
                ? ruleResponse?.documentTitle
                : title,
            targetDocumentId: moved ? ruleResponse?.targetDocumentId : null,
            targetDocumentTitle: moved ? ruleResponse?.documentTitle : null,
            appliedRuleTitle: ruleResponse?.appliedRuleTitle ?? null,
            outcome: duplicateRejected
              ? 'rejected'
              : ruleResponse?.error
                ? 'error'
                : moved
                  ? 'moved'
                  : 'added',
            isDuplicate: uploaded.isDuplicate === true ||
              ruleResponse?.duplicateSkipped === true,
            error: uploaded.duplicateSkipped
              ? this.i18n.tr('documents.form.duplicateFileSkippedWarning')
              : ruleResponse?.error ?? null,
          });
        } catch (error: unknown) {
          await this.recordImportProtocolEntry(protocolNumber, {
            originalFileName: file.name,
            startedAt,
            completedAt: new Date().toISOString(),
            sourceDocumentId: doc?.id ?? null,
            sourceDocumentTitle: title,
            currentDocumentId: doc?.id ?? null,
            currentDocumentTitle: title,
            outcome: 'error',
            isDuplicate: uploaded?.isDuplicate === true,
            error: error instanceof Error ? error.message : String(error),
          });
          throw error;
        }
      }
      await this.fetchDocuments();
      if (this.showChronology) await this.reloadChronologyTree();
      if (duplicateResults.length > 0) {
        await ConfirmDialog.alertDuplicateResults(
          duplicateResults,
          this.i18n.tr('documents.form.duplicateFileTitle'),
          this.i18n.tr('documents.form.duplicateFileSummary'),
        );
      }
    } catch (err: unknown) {
      await ConfirmDialog.alert(
        err instanceof Error ? err.message : this.i18n.tr('documents.dropZone.createError'),
        this.i18n.tr('common.error'),
      );
    } finally {
      if (protocolNumber !== null) {
        await this.finishImportProtocol(protocolNumber);
      }
      this.isBatchCreating = false;
    }
  }

  private async startImportProtocol(): Promise<number> {
    const protocol = await createImportProtocol();
    if (!Number.isSafeInteger(protocol.number) || protocol.number <= 0)
      throw new Error(this.i18n.tr('importProtocols.invalidNumber'));
    return protocol.number;
  }

  private async finishImportProtocol(number: number): Promise<void> {
    try {
      await completeImportProtocol(number);
    } catch (error: unknown) {
      console.error('[Radish DMS] Could not complete import protocol', {
        protocolNumber: number,
        error,
      });
    }
  }

  private async recordImportProtocolEntry(
    number: number,
    entry: Parameters<typeof addImportProtocolEntry>[1],
  ): Promise<void> {
    try {
      await addImportProtocolEntry(number, entry);
    } catch (error: unknown) {
      console.error('[Radish DMS] Could not record import protocol entry', {
        protocolNumber: number,
        fileName: entry.originalFileName,
        error,
      });
    }
  }

  // ── Chronology tree ────────────────────────────────────────────────────────

  public async toggleChronology(): Promise<void> {
    this.showChronology = !this.showChronology;
    this._saveUiState();
    if (this.showChronology && !this.chronologyTree) {
      await this.reloadChronologyTree();
    }
  }

  private _saveUiState(): void {
    const uiState = this._getUiState();
    localStorage.setItem(UI_STATE_KEY, JSON.stringify(uiState));
    this._schedulePreferenceSync();
  }

  private _getUiState(): PersistedDocumentUiState {
    return {
      showChronology: this.showChronology,
      showFilterBar: this.showFilterBar,
      ownedOnly: this.ownedOnly,
      filterText: this.filterText,
      filterIncludeDescription: this.filterIncludeDescription,
      filterIncludeFileText: this.filterIncludeFileText,
      includeFormOnly: this.includeFormOnly,
      filterTags: this.filterTags,
      shareFilterType: this.shareFilterType,
      shareFilterUsers: this.shareFilterUsers,
      viewMode: this.viewMode,
      sortBy: this.sortBy,
      sortDescending: this.sortDescending,
    };
  }

  private _loadCachedPreferences(): { hasColumns: boolean; hasUiState: boolean } {
    let hasColumns = false;
    let hasUiState = false;

    const savedCols = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (savedCols) {
      hasColumns = true;
      this._applyColumnsSetting(savedCols);
    }

    const savedUi = localStorage.getItem(UI_STATE_KEY);
    if (savedUi) {
      hasUiState = true;
      this._applyUiStateSetting(savedUi);
    }

    return { hasColumns, hasUiState };
  }

  private _applyColumnsSetting(value: string): void {
    try {
      const parsed = JSON.parse(value) as Record<string, boolean>;
      this.visibleColumns = { ...DEFAULT_COLUMNS, ...parsed };
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(this.visibleColumns));
    } catch {
      // Ignore malformed preference data and keep the defaults.
    }
  }

  private _applyUiStateSetting(value: string): void {
    try {
      const ui = JSON.parse(value) as PersistedDocumentUiState;
      if (typeof ui.showChronology === 'boolean') this.showChronology = ui.showChronology;
      if (typeof ui.showFilterBar === 'boolean') this.showFilterBar = ui.showFilterBar;
      if (typeof ui.ownedOnly === 'boolean') this.ownedOnly = ui.ownedOnly;
      if (typeof ui.filterText === 'string') this.filterText = ui.filterText;
      if (typeof ui.filterIncludeDescription === 'boolean') this.filterIncludeDescription = ui.filterIncludeDescription;
      if (typeof ui.filterIncludeFileText === 'boolean') this.filterIncludeFileText = ui.filterIncludeFileText;
      if (typeof ui.includeFormOnly === 'boolean') this.includeFormOnly = ui.includeFormOnly;
      if (Array.isArray(ui.filterTags)) this.filterTags = ui.filterTags;
      if (ui.shareFilterType === 'from' || ui.shareFilterType === 'with') this.shareFilterType = ui.shareFilterType;
      else if (ui.shareFilterType === 'none') {
        this.shareFilterType = 'none';
        this._setShareFilterUsers([]);
      }
      if (Array.isArray(ui.shareFilterUsers)) this._setShareFilterUsers(ui.shareFilterUsers);
      if (ui.viewMode === 'tiles' || ui.viewMode === 'list') this.viewMode = ui.viewMode;
      if (typeof ui.sortBy === 'string' && ui.sortBy) this.sortBy = ui.sortBy;
      if (typeof ui.sortDescending === 'boolean') this.sortDescending = ui.sortDescending;
      localStorage.setItem(UI_STATE_KEY, JSON.stringify(this._getUiState()));
    } catch {
      // Ignore malformed preference data and keep the defaults.
    }
  }

  private _schedulePreferenceSync(): void {
    if (this._preferenceSyncTimer !== null) clearTimeout(this._preferenceSyncTimer);
    this._preferenceSyncTimer = setTimeout(() => {
      this._preferenceSyncTimer = null;
      void this._syncPreferences();
    }, 500);
  }

  private async _syncPreferences(): Promise<void> {
    const operations = [
      upsertUserSetting(COLUMNS_SETTING_KEY, JSON.stringify(this.visibleColumns)),
      upsertUserSetting(UI_STATE_SETTING_KEY, JSON.stringify(this._getUiState())),
    ];
    await Promise.all(operations.map(operation => operation.catch(() => {
      // Preferences are best-effort; local storage keeps the current browser usable.
    })));
  }

  public async reloadChronologyTree(): Promise<void> {
    try {
      const searchText = this.filterText.trim() || undefined;
      this.chronologyTree = await getChronologyTree(
        searchText, this.filterIncludeDescription,
        this._filterTagIds.length > 0 ? this._filterTagIds : undefined,
        this.ownedOnly,
        this._shareFilterTypeValue,
        this._shareFilterUsernames,
        this.filterIncludeFileText,
        this.includeFormOnly,
      );
    } catch { /* ignore */ }
  }

  // ── Infinite scroll ────────────────────────────────────────────────────────
  public scrollContainer: HTMLElement | null = null;
  public scrollSentinel: HTMLElement | null = null;
  public isLoadingMore = false;

  private _scrollObserver: IntersectionObserver | null = null;

  public get hasMore(): boolean {
    return this.documents.length < this.totalCount;
  }

  public detaching(): void {
    this._scrollObserver?.disconnect();
    this._scrollObserver = null;
    if (this._preferenceSyncTimer !== null) {
      clearTimeout(this._preferenceSyncTimer);
      this._preferenceSyncTimer = null;
      void this._syncPreferences();
    }
    this._localeSub?.dispose();
    document.removeEventListener('dragenter', this._onGlobalDragEnter);
    document.removeEventListener('dragover', this._onGlobalDragOver);
    document.removeEventListener('dragleave', this._onGlobalDragLeave);
    document.removeEventListener('drop', this._onGlobalDrop);
    document.removeEventListener('click', this._selectionDocumentHandler);
  }

  private async _loadMore(): Promise<void> {
    if (this.isFetching || this.isLoadingMore || !this.hasMore) return;
    this.isLoadingMore = true;
    this.page++;
    try {
      const { dateFrom, dateTo } = this.selectionDateRange();
      const searchText = this.filterText.trim() || undefined;
      const shareFilterType = this._shareFilterTypeValue;
      const shareFilterUsernames = this._shareFilterUsernames;
      const result = await listDocuments(
        this.sortBy, this.sortDescending, this.page, this.pageSize,
        dateFrom, dateTo,
        searchText, this.filterIncludeDescription,
        this._filterTagIds.length > 0 ? this._filterTagIds : undefined,
        this.ownedOnly,
        shareFilterType,
        shareFilterUsernames,
        this.filterIncludeFileText,
        this.includeFormOnly,
      );
      this.documents = [...this.documents, ...result.documents];
      this.totalCount = result.totalCount;
    } catch {
      this.page--;
    } finally {
      this.isLoadingMore = false;
    }
  }

  public toggleYear(year: number): void {
    this.expandedYears = { ...this.expandedYears, [year]: !this.expandedYears[year] };
  }

  public toggleMonth(year: number, month: number): void {
    const key = `${year}-${month}`;
    this.expandedMonths = { ...this.expandedMonths, [key]: !this.expandedMonths[key] };
  }

  public selectAll(): void {
    this.selType = 'all';
    this.page = 1;
    void this.fetchDocuments();
  }

  public selectYear(year: number): void {
    this.selType = 'year';
    this.selYear = year;
    this.page = 1;
    void this.fetchDocuments();
  }

  public selectMonth(year: number, month: number): void {
    this.selType = 'month';
    this.selYear = year;
    this.selMonth = month;
    this.page = 1;
    void this.fetchDocuments();
  }

  public onYearClick(year: number): void {
    this.toggleYear(year);
    this.selType = 'year';
    this.selYear = year;
    this.page = 1;
    void this.fetchDocuments();
  }

  public onMonthClick(year: number, month: number): void {
    this.toggleMonth(year, month);
    this.selType = 'month';
    this.selYear = year;
    this.selMonth = month;
    this.page = 1;
    void this.fetchDocuments();
  }

  public selectDay(year: number, month: number, day: number): void {
    this.selType = 'day';
    this.selYear = year;
    this.selMonth = month;
    this.selDay = day;
    this.page = 1;
    void this.fetchDocuments();
  }

  public get breadcrumbItems(): Array<{ label: string; type: 'all' | 'year' | 'month' | 'day'; isLast: boolean }> {
    const locale = this._locale; // tracked — re-evaluates this getter when locale changes
    const allLabel = this.i18n.tr('documents.chronology.all');
    const items: Array<{ label: string; type: 'all' | 'year' | 'month' | 'day'; isLast: boolean }> = [
      { label: allLabel, type: 'all', isLast: this.selType === 'all' },
    ];
    if (this.selType === 'year' || this.selType === 'month' || this.selType === 'day') {
      items.push({ label: String(this.selYear), type: 'year', isLast: this.selType === 'year' });
    }
    if (this.selType === 'month' || this.selType === 'day') {
      items.push({ label: this.monthName(this.selMonth), type: 'month', isLast: this.selType === 'month' });
    }
    if (this.selType === 'day') {
      items.push({ label: String(this.selDay) + '.', type: 'day', isLast: true });
    }
    return items;
  }

  public onBreadcrumbClick(type: 'all' | 'year' | 'month' | 'day'): void {
    if (type === 'all') this.selectAll();
    else if (type === 'year') this.selectYear(this.selYear);
    else if (type === 'month') this.selectMonth(this.selYear, this.selMonth);
    // day = current, no navigation needed
  }

  public monthName(month: number): string {
    return new Date(2000, month - 1, 1).toLocaleString(this._locale || 'en', { month: 'long' });
  }

  public securityLevelLabel(level: SecurityLevel): string {
    const sl = SECURITY_LEVELS.find(s => s.value === level);
    return sl ? this.i18n.tr(sl.key) : String(level);
  }

  public securityLevelClass(level: SecurityLevel): string {
    switch (level) {
      case 0: return 'public';
      case 1: return 'internal';
      case 2: return 'confidential';
      case 3: return 'restricted';
      default: return 'internal';
    }
  }

  public tagOverflowTooltip(tags: Tag[]): string {
    return tags.map(t => t.name).join(', ');
  }

  public toggleViewMode(): void {
    this.viewMode = this.viewMode === 'list' ? 'tiles' : 'list';
    this._saveUiState();
  }

  public filePreviewUrl(docId: string, fileId: string, uploadedAt?: string): string {
    const v = uploadedAt ? `?v=${encodeURIComponent(uploadedAt)}` : '';
    return apiUrl(`/documents/${docId}/files/${fileId}/preview${v}`);
  }

  private selectionDateRange(): { dateFrom?: string; dateTo?: string } {
    if (this.selType === 'all') return {};
    if (this.selType === 'year') {
      return { dateFrom: `${this.selYear}-01-01`, dateTo: `${this.selYear + 1}-01-01` };
    }
    if (this.selType === 'month') {
      const nextMonth = this.selMonth === 12
        ? `${this.selYear + 1}-01`
        : `${this.selYear}-${String(this.selMonth + 1).padStart(2, '0')}`;
      return {
        dateFrom: `${this.selYear}-${String(this.selMonth).padStart(2, '0')}-01`,
        dateTo:   `${nextMonth}-01`,
      };
    }
    if (this.selType === 'day') {
      const pad = (n: number) => String(n).padStart(2, '0');
      const next = new Date(this.selYear, this.selMonth - 1, this.selDay + 1);
      return {
        dateFrom: `${this.selYear}-${pad(this.selMonth)}-${pad(this.selDay)}`,
        dateTo:   `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`,
      };
    }
    return {};
  }
}
